import { useState, useEffect } from 'react';
import api, { adminGetSystemNotice, adminSetSystemNotice } from '../services/api';
import { Crown, Buildings, Users, Receipt, Robot, Megaphone, CheckCircle, Warning } from '@phosphor-icons/react';

export default function SuperAdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/stats')
      .then(res => setStats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-700"></div>
      </div>
    );
  }

  const cards = [
    { label: 'Mandanten gesamt', value: stats?.mandanten_total, sub: `${stats?.mandanten_active} aktiv`, icon: Buildings, color: 'violet' },
    { label: 'User insgesamt', value: stats?.users_total, sub: 'Vermieter + Super-Admins', icon: Users, color: 'blue' },
    { label: 'Immobilien', value: stats?.properties_total, sub: 'über alle Mandanten', icon: Buildings, color: 'emerald' },
    { label: 'Belege', value: stats?.receipts_total, sub: 'hochgeladen', icon: Receipt, color: 'amber' },
    { label: 'KI-Anfragen', value: stats?.ai_calls_total, sub: 'Lifetime', icon: Robot, color: 'rose' },
  ];

  const colorMap = {
    violet: 'bg-violet-50 border-violet-200 text-violet-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
  };

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      <div className="flex items-center gap-3">
        <Crown weight="fill" className="w-8 h-8 text-amber-400" />
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
            Plattform-Übersicht
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Super-Admin Dashboard – Mandanten & Statistiken</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c, idx) => (
          <div key={idx} className={`border p-5 ${colorMap[c.color]}`} data-testid={`sa-stat-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-2 mb-3">
              <c.icon weight="bold" className="w-5 h-5" />
              <p className="text-xs uppercase tracking-widest font-bold">{c.label}</p>
            </div>
            <p className="font-mono text-3xl sm:text-4xl font-black tracking-tighter">{c.value ?? 0}</p>
            <p className="text-xs mt-1 opacity-75">{c.sub}</p>
          </div>
        ))}
      </div>

      <SystemNoticeEditor />

      <div className="bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
        <p className="font-medium">Was kann ich hier tun?</p>
        <ul className="mt-2 space-y-1 list-disc list-inside text-xs">
          <li>Im Menü <strong>Mandanten</strong>: Mandanten anlegen, deaktivieren, Mandant-Admin-Konten erstellen</li>
          <li>Im Menü <strong>Sicherheits-Audit</strong>: Login-Versuche, System-Status, Aktivitäts-Log einsehen</li>
          <li>Über „Zur Mandanten-Ansicht" wechseln Sie in Ihre eigene Mandant-Sicht (Immobilien, Belege etc.)</li>
        </ul>
      </div>
    </div>
  );
}

function SystemNoticeEditor() {
  const [active, setActive] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');
  const [scheduledFor, setScheduledFor] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    adminGetSystemNotice()
      .then(res => {
        const d = res.data || {};
        setActive(!!d.active);
        setMessage(d.message || '');
        setSeverity(d.severity || 'info');
        setScheduledFor(d.scheduled_for || '');
      })
      .catch(() => setError('Konnte aktuellen Hinweis nicht laden'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await adminSetSystemNotice({
        active,
        message: message.trim(),
        severity,
        scheduled_for: scheduledFor || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-violet-200 rounded-lg" data-testid="system-notice-editor">
      <div className="px-5 py-3 border-b border-violet-100 bg-violet-50/40 flex items-center gap-2">
        <Megaphone weight="bold" className="w-5 h-5 text-violet-700" />
        <h2 className="font-heading text-base sm:text-lg font-bold text-violet-900">Wartungs-Hinweis / Update-Banner</h2>
      </div>

      <div className="p-5 space-y-4">
        {loading ? (
          <div className="h-12 flex items-center text-slate-400 text-sm">Lade aktuellen Hinweis…</div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center cursor-pointer gap-3" data-testid="notice-toggle-label">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  className="sr-only peer"
                  data-testid="notice-toggle-input"
                />
                <div className="relative w-12 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:bg-emerald-500 transition-colors">
                  <div className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full transition-transform ${active ? 'translate-x-6' : ''}`} />
                </div>
                <span className="text-sm font-medium text-slate-900">
                  Hinweis ist {active ? <span className="text-emerald-700">AKTIV</span> : <span className="text-slate-500">deaktiviert</span>}
                </span>
              </label>
            </div>

            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">
                Hinweis-Text
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="z.B. Am 15.02.2026 um 22:00 führen wir ein App-Update durch. Die App ist ca. 15 Minuten nicht erreichbar."
                className="w-full border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 outline-none rounded"
                data-testid="notice-message-input"
              />
              <p className="text-xs text-slate-400 mt-1">{message.length}/1000 Zeichen</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Schweregrad</label>
                <select
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                  className="w-full border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 outline-none rounded"
                  data-testid="notice-severity-select"
                >
                  <option value="info">Info (blau)</option>
                  <option value="warning">Warnung (gelb)</option>
                  <option value="critical">Kritisch (rot)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Geplant für (optional)</label>
                <input
                  type="date"
                  value={scheduledFor || ''}
                  onChange={(e) => setScheduledFor(e.target.value)}
                  className="w-full border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 outline-none rounded"
                  data-testid="notice-date-input"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 text-sm flex items-center gap-2">
                <Warning weight="bold" className="w-4 h-4" /> {error}
              </div>
            )}

            {saved && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 text-sm flex items-center gap-2">
                <CheckCircle weight="fill" className="w-4 h-4" /> Hinweis gespeichert · {active ? 'wird allen Usern angezeigt' : 'derzeit deaktiviert'}
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="bg-violet-700 text-white px-4 py-2 text-sm font-medium hover:bg-violet-800 disabled:opacity-50 rounded"
                data-testid="notice-save-button"
              >
                {saving ? 'Speichert…' : 'Hinweis speichern'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
